import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Icon,
  TextField,
} from "@mui/material";
import LGAutocomplete from "../../../atoms/Common/LGAutocomplete.tsx";
import { getProductList } from "../../../api/products";
import { FastField, useFormikContext } from "formik";
import { LGNumber } from "../../../atoms/Inputs/LGNumber.tsx";

interface FormValues {
  inbound: {
    companyId: number | null;
    description: string;
    warehouseId: number | null;
    isNational: boolean;
  };
  inboundItems: Array<{
    description: string;
    deliveryNo: number;
    quantityDelivered: number;
    weight: number;
    productId: number | null;
    productCategoryId: number | null;
  }>;
}

interface InboundItemsNoPOProps {
  formMode: string;
  companyId?: number;
}

const InboundItemsNoPO: React.FC<InboundItemsNoPOProps> = ({
  formMode,
  companyId,
}) => {
  const [items, setItems] = useState<any[]>([]);
  const { values, setFieldValue, errors, touched } =
    useFormikContext<FormValues>();

  const handleAddItem = () => {
    const newItem = {
      description: "",
      deliveryNo: 1,
      quantityDelivered: 0,
      weight: 0,
      productId: null,
      productCategoryId: null,
    };

    setItems((prev) => [...prev, newItem]);

    // Also update the formik values
    const currentItems = values.inboundItems || [];
    setFieldValue("inboundItems", [...currentItems, newItem]);
  };

  useEffect(() => {
    if (formMode === "edit") {
      setItems(values.inboundItems || []);
    }
  }, [formMode, values.inboundItems]);

  const handleRemoveItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);

    // Update formik values
    setFieldValue("inboundItems", newItems);
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);

    // Update formik values
    setFieldValue(`inboundItems[${index}].${field}`, value);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Productos
      </Typography>

      {items.map((item, index) => {
        const itemError = errors.inboundItems?.[index];
        const itemTouched = touched.inboundItems?.[index];
        const quantityError =
          itemError &&
          typeof itemError === "object" &&
          "quantityDelivered" in itemError
            ? (itemError as any).quantityDelivered
            : undefined;
        const weightError =
          itemError &&
          typeof itemError === "object" &&
          "weight" in itemError
            ? (itemError as any).weight
            : undefined;

        return (
          <Box
            pb={2}
            my={1}
            key={index}
            sx={{ border: "1px solid #aaaaaa", borderRadius: 1, p: 2 }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={11}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LGAutocomplete
                      fetchFunction={getProductList}
                      optionLabel={["code", "description"]}
                      optionSeparator=" -- "
                      queryParams={companyId ? { companyId } : {}}
                      onChange={(event, value) => {
                        setFieldValue(
                          `inboundItems[${index}].productId`,
                          value ? value.id : null
                        );
                      }}
                      label="Producto"
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <FastField
                      name={`inboundItems[${index}].quantityDelivered`}
                      as={LGNumber}
                      label="Cantidad"
                      type="number"
                      fullWidth
                      error={
                        itemTouched?.quantityDelivered && Boolean(quantityError)
                      }
                      helperText={
                        itemTouched?.quantityDelivered ? quantityError : undefined
                      }
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <FastField
                      name={`inboundItems[${index}].weight`}
                      as={LGNumber}
                      label="Peso (kg)"
                      type="number"
                      fullWidth
                      error={itemTouched?.weight && Boolean(weightError)}
                      helperText={itemTouched?.weight ? weightError : undefined}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FastField
                      name={`inboundItems[${index}].description`}
                      as={TextField}
                      label="Descripción"
                      multiline
                      rows={2}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={1}>
                <IconButton onClick={() => handleRemoveItem(index)} color="error">
                  <Icon>delete</Icon>
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        );
      })}
      <Button variant="contained" onClick={handleAddItem}>
        Agregar producto
      </Button>
    </Box>
  );
};

export default InboundItemsNoPO;
